import React, { useEffect, useState } from "react";
import Header from "@components/Common/Header/Header";
import Footer from "@components/Common/Footer/Footer";
import {
  getTermsAndConditions,
  TermsAndConditions as TermsAndConditionsData,
  TermsAndConditionsResponse,
} from "../../API/TermsAndConditions";
import { formatPolicyContent } from "@utils/formatPolicyContent";
import "../PrivacyPolicy/Privacy.css";

const formatLastUpdated = (value?: string | null) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const TermsHeader = ({
  title,
  lastUpdated,
}: {
  title: string;
  lastUpdated: string | null;
}) => (
  <>
    <h1 className="privacy-policy-title">{title}</h1>
    {lastUpdated && (
      <p className="privacy-policy-meta">Last Updated: {lastUpdated}</p>
    )}
  </>
);

const TermsFallback = () => (
  <>
    <h2>1. Introduction</h2>
    <p>
      Welcome to Lendriva. These Terms and Conditions govern your access to and
      use of our platform, including all services offered to borrowers and
      lenders. By creating an account or using our services, you agree to be
      bound by these terms.
    </p>
    <p>
      Please read these terms carefully before using the platform. If you do not
      agree with any part of these terms, you must not use our services.
    </p>

    <h2>2. Eligibility</h2>
    <p>To use Lendriva, you must:</p>
    <ul>
      <li>Be at least 18 years of age.</li>
      <li>Be legally capable of entering into binding contracts.</li>
      <li>Provide accurate and complete registration information.</li>
      <li>Complete any required identity, credit, or compliance checks.</li>
    </ul>

    <h2>3. Use of the Platform</h2>
    <p>
      You agree to use the platform only for lawful purposes and in accordance
      with these terms. You must not misuse the platform, attempt unauthorised
      access, or engage in activity that could harm Lendriva, its users, or third
      parties.
    </p>

    <h3>3.1 Borrower obligations</h3>
    <ul>
      <li>Provide truthful information in loan applications.</li>
      <li>Repay loans in accordance with agreed terms and schedules.</li>
      <li>Notify us promptly of any material change to your circumstances.</li>
    </ul>

    <h3>3.2 Lender obligations</h3>
    <ul>
      <li>Review offer terms carefully before committing funds.</li>
      <li>Comply with applicable financial regulations and platform rules.</li>
      <li>Use the platform only for legitimate lending activity.</li>
    </ul>

    <h2>4. Fees, Payments and Wallet Services</h2>
    <p>
      Certain services may involve fees, transaction charges, or third-party
      payment processing. All applicable fees will be disclosed before you
      confirm a transaction. You are responsible for maintaining sufficient
      wallet balance where required.
    </p>

    <h2>5. Limitation of Liability</h2>
    <p>
      To the fullest extent permitted by law, Lendriva shall not be liable for
      indirect, incidental, special, or consequential losses arising from your
      use of the platform. Nothing in these terms excludes liability that cannot
      be excluded under applicable law.
    </p>

    <h2>6. Changes to These Terms</h2>
    <p>
      We may update these Terms and Conditions from time to time. Updated terms
      will be posted on this page with a revised date. Continued use of the
      platform after changes take effect constitutes acceptance of the updated
      terms.
    </p>

    <h2>7. Contact Us</h2>
    <p>
      If you have any questions about these Terms and Conditions, please contact
      us at{" "}
      <a href="mailto:support@lendriva.com">support@lendriva.com</a>.
    </p>
  </>
);

const TermsAndConditions = () => {
  const handleMenuToggle = () => {};
  const [terms, setTerms] = useState<TermsAndConditionsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    getTermsAndConditions()
      .then((res: TermsAndConditionsResponse) => setTerms(res.data))
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, []);

  const title = terms?.title || "Terms and Conditions";
  const lastUpdated = formatLastUpdated(terms?.updatedAt);

  return (
    <div className="privacy-policy-page">
      <Header handleMenuToggle={handleMenuToggle} />

      <section className="privacy-policy-content">
        <div className="container">
          {isLoading && (
            <p className="privacy-policy-loading">
              Loading terms and conditions...
            </p>
          )}

          {!isLoading && terms?.content && (
            <div className="privacy-policy-body">
              <TermsHeader title={title} lastUpdated={lastUpdated} />
              <div
                className="privacy-policy-html"
                dangerouslySetInnerHTML={{
                  __html: formatPolicyContent(terms.content),
                }}
              />
            </div>
          )}

          {!isLoading && !terms?.content && (
            <div className="privacy-policy-body">
              <TermsHeader title={title} lastUpdated={lastUpdated} />
              {isError && (
                <p className="privacy-policy-error">
                  Unable to load the latest terms and conditions. Showing
                  default content below.
                </p>
              )}
              <TermsFallback />
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
